import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";

import LogoutIcon from "@mui/icons-material/Logout";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "./AdminPainel.css";

/* ———————————————————————————— CONSTANTES ———————————————————————————— */
/** Lista somente com papéis realmente aceitos pelo backend */
const ROLES_VALIDAS = [
  "admin",
  "suporte",
  "logistica",
  "apr",
  "aprviewer",
  "aprgrafico",
  "comercial",
  "rh",
  "financeiro",
  "financeiroadm",
  "ti",
  "infra",
  "compras",
  "compras-admin",
  // Só entra no select de vendedor do painel-score; não abre módulo nenhum.
  "vendedor",
  "marketing",
  // Alias antigo do Marketing Center (/marketing)
  "supervisor",
  // Inbox de solicitacoes internas
  "noc",
  "desenvolvimento",
];

/** axios via Nginx (/auth -> :7007) */
const api = axios.create({
  baseURL: "/auth",
  withCredentials: true,
});

/* ———————————————————————————— COMPONENTE PRINCIPAL ———————————————————————————— */
export default function AdminPanel() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const nomeCompleto = localStorage.getItem("userNome") || "";
  const firstName = (nomeCompleto.split(" ")[0] || "Usuário").trim();

  /* ────────────── FETCH DE USUÁRIOS ────────────── */
  useEffect(() => {
    api
      .get("/admin/users")
      .then((resp) => {
        setUsuarios(Array.isArray(resp.data) ? resp.data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Erro ao buscar usuários:", err);
        const st = err.response?.status;
        if (st === 401) setErrorMsg("Sessão expirada. Faça login novamente.");
        else if (st === 403) setErrorMsg("Acesso negado: você não é administrador.");
        else setErrorMsg("Falha ao carregar lista de usuários.");
        setLoading(false);
      });
  }, []);


  useEffect(() => {
    const SCRIPT_ID = "sonik-sidebar-script";
    const src = "/apr-admin/shared/sidebar.js";
  
    function boot() {
      // chama a função global do sidebar.js
      if (typeof window.initSidebar === "function") {
        window.initSidebar();
  
        // se o seu sidebar usa o botão #btnHamb, garante o hook
        // (alguns sidebars já escutam isso automaticamente; aqui é “garantia”)
        const btn = document.getElementById("btnHamb");
        if (btn && typeof window.toggleSidebar === "function") {
          btn.onclick = () => window.toggleSidebar();
        }
      }
    }
  
    // se já carregou antes, só inicializa
    if (document.getElementById(SCRIPT_ID)) {
      boot();
      return;
    }
  
    // senão, injeta o script
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = src;
    s.defer = true;
    s.onload = boot;
    document.body.appendChild(s);
  
    // sem cleanup (normalmente sidebar é global e pode ficar)
  }, []);
  
  /* ────────────── HANDLERS ────────────── */
  function handleLogout() {
    localStorage.clear();
    window.location.href = "/";
  }
  function handleBackToDashboard() {
    window.location.href = "/dashboard";
  }

  const usuariosFiltrados = usuarios.filter((u) => {
    const termo = searchTerm.trim().toLowerCase();
    if (!termo) return true;
    return (
      (u.nome || "").toLowerCase().includes(termo) ||
      (u.email || "").toLowerCase().includes(termo)
    );
  });

  /* ────────────── ESTADOS DE CARREGAMENTO/ERRO ────────────── */
  if (loading) {
    return (
      <TelaFundo>
        <CircularProgress sx={{ color: "#1565c0" }} />
      </TelaFundo>
    );
  }
  if (errorMsg) {
    return (
      <TelaFundo>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 400,
            textAlign: "center",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 12px 40px rgba(21, 101, 192, 0.12)",
          }}
        >
          <Typography variant="h6" sx={{ color: "#dc2626", mb: 2, fontWeight: 600 }}>
            {errorMsg}
          </Typography>
          <Button
            onClick={handleBackToDashboard}
            sx={{
              bgcolor: "#1565c0",
              color: "white",
              px: 3,
              py: 1.5,
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { bgcolor: "#0d47a1" },
            }}
          >
            Voltar ao Dashboard
          </Button>
        </Paper>
      </TelaFundo>
    );
  }

  /* ────────────── UI ────────────── */
  return (
    <Box
      className="admin-painel-v2"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: "linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#1565c0",
          color: "white",
          px: 3,
          py: 2,
          flexShrink: 0,
          boxShadow: "0 4px 20px rgba(21, 101, 192, 0.3)",
        }}
      >
        <IconButton onClick={handleBackToDashboard} sx={{ color: "white", mr: 1, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h5" sx={{ flexGrow: 1, textAlign: "center", fontWeight: 600, letterSpacing: "-0.02em" }}>
          Bem-vindo, {firstName} (Admin)
        </Typography>

        <IconButton onClick={handleLogout} sx={{ color: "white", ml: 1, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
          <LogoutIcon />
        </IconButton>
      </Box>

      {/* Conteúdo */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            maxHeight: `calc(100vh - 120px)`,
            bgcolor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 4px 24px rgba(21, 101, 192, 0.08)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            border: "1px solid rgba(21, 101, 192, 0.1)",
          }}
        >
          {/* Topo do card */}
          <Box sx={{ p: 4, flexShrink: 0, borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ color: "#0f172a", fontWeight: 700, letterSpacing: "-0.02em" }}>
              Painel Administrativo
            </Typography>
            <Typography variant="body1" align="center" sx={{ mb: 3, color: "#64748b", fontSize: "15px" }}>
              Aqui você pode gerenciar as permissões dos usuários cadastrados.
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f8fafc",
                  borderRadius: "12px",
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: "#1565c0", borderWidth: "1px" },
                  "&.Mui-focused fieldset": { borderColor: "#1565c0", borderWidth: "2px" },
                },
                "& .MuiInputBase-input": { color: "#0f172a" },
              }}
            />
          </Box>

          {/* Tabela rolável */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              flex: 1,
              overflowY: "auto",
              bgcolor: "transparent",
            }}
          >
            <Table stickyHeader aria-label="Tabela de usuários">
              <TableHead>
                <CabecalhoTable />
              </TableHead>

              <TableBody>
                {usuariosFiltrados.map((u) => (
                  <UsuarioRow
                    key={u.email}
                    userData={u}
                    onUpdate={(upd) =>
                      setUsuarios((old) =>
                        old.map((x) => (x.email === upd.email ? upd : x))
                      )
                    }
                  />
                ))}

                {!usuariosFiltrados.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "#64748b", py: 8, fontSize: "15px" }}>
                      Nenhum usuário encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}

/* ———————————————————————————— COMPONENTES AUXILIARES ———————————————————————————— */
const TelaFundo = ({ children }) => (
  <Box
    className="admin-painel-v2"
    sx={{
      display: "flex",
      height: "100vh",
      width: "100vw",
      background: "linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%)",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </Box>
);

const CabecalhoTable = () => (
  <TableRow sx={{ "& th": { borderBottom: "none", bgcolor: "#1565c0", color: "white", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", py: 2 } }}>
    {["Email", "Nome", "Login", "Permissões", "Ações"].map((h) => (
      <TableCell key={h}>{h}</TableCell>
    ))}
  </TableRow>
);

/* ———————————————————————————— LINHA DE USUÁRIO ———————————————————————————— */
function UsuarioRow({ userData, onUpdate }) {
  const permissoesInit = Array.isArray(userData.permissoes)
    ? userData.permissoes
    : [];
  const [editando, setEditando] = useState(false);
  const [permTemp, setPermTemp] = useState([...permissoesInit]);
  const [salvando, setSalvando] = useState(false);

  /** Normaliza/valida antes de enviar */
  function sanitizePerms(perms) {
    const norm = (perms || [])
      .map((p) => String(p || "").toLowerCase().trim())
      .filter((p) => ROLES_VALIDAS.includes(p));
    // remove duplicados
    return Array.from(new Set(norm));
  }

  async function salvarAlteracoes() {
    setSalvando(true);
    const cleanPerms = sanitizePerms(permTemp);

    try {
      await api.put(
        `/admin/users/${encodeURIComponent(userData.email)}/permissoes`,
        { permissoes: cleanPerms }
      );

      onUpdate({ ...userData, permissoes: cleanPerms });
      setEditando(false);
    } catch (e) {
      console.error(e);
      const serverMsg =
        e.response?.data?.message ||
        e.response?.data?.error ||
        e.response?.data ||
        "Falha ao salvar permissões.";
      alert(`Erro ao salvar: ${serverMsg}`);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <TableRow
      hover
      sx={{
        "& td": { color: "#334155", borderBottom: "1px solid #e2e8f0" },
        "&:hover": { bgcolor: "rgba(21, 101, 192, 0.04)" },
        "&:nth-of-type(even)": { bgcolor: "#f8fafc" },
        "&:nth-of-type(even):hover": { bgcolor: "rgba(21, 101, 192, 0.06)" },
        "&:last-child td": { borderBottom: "none" },
      }}
    >
      <TableCell sx={{ color: "#0f172a", fontWeight: 500 }}>{userData.email}</TableCell>
      <TableCell sx={{ color: "#334155" }}>{userData.nome}</TableCell>
      <TableCell sx={{ color: "#334155" }}>{userData.login}</TableCell>

      {/* PERMISSÕES */}
      <TableCell>
        {editando ? (
          <FormControl
            size="small"
            sx={{
              width: 280,
              "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: "10px" },
              "& .MuiInputLabel-root": { color: "#64748b" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#1565c0" },
              "& .MuiOutlinedInput-root fieldset": { borderColor: "#e2e8f0" },
              "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "#1565c0" },
              "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#1565c0", borderWidth: "2px" },
            }}
          >
            <InputLabel id={`label-${userData.email}`}>Permissões</InputLabel>
            <Select
              labelId={`label-${userData.email}`}
              multiple
              value={permTemp}
              onChange={(e) => setPermTemp(e.target.value)}
              input={<OutlinedInput label="Permissões" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((p) => (
                    <Chip key={p} label={p} size="small" sx={{ bgcolor: "#1565c0", color: "white" }} />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: "#ffffff",
                    boxShadow: "0 12px 40px rgba(21, 101, 192, 0.12)",
                    borderRadius: "12px",
                    mt: 1.5,
                    "& .MuiMenuItem-root": { color: "#334155" },
                    "& .MuiMenuItem-root:hover": { bgcolor: "rgba(21, 101, 192, 0.08)" },
                  },
                },
              }}
            >
              {ROLES_VALIDAS.map((perm) => (
                <MenuItem key={perm} value={perm}>
                  {perm === 'aprgrafico' ? 'aprgrafico — só o gráfico' : perm}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" sx={{ fontStyle: "italic", color: "#64748b" }}>
            {permissoesInit.length ? permissoesInit.join(", ") : "— sem permissões —"}
          </Typography>
        )}
      </TableCell>

      {/* AÇÕES */}
      <TableCell>
        {editando ? (
          <>
            <Button
              variant="contained"
              size="small"
              onClick={salvarAlteracoes}
              disabled={salvando}
              sx={{
                mr: 1,
                bgcolor: "#1565c0",
                color: "white",
                borderRadius: "10px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover:not(:disabled)": { bgcolor: "#0d47a1" },
              }}
            >
              {salvando ? "Salvando…" : "Salvar"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPermTemp([...permissoesInit]);
                setEditando(false);
              }}
              disabled={salvando}
              sx={{
                borderColor: "#94a3b8",
                color: "#64748b",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": { borderColor: "#64748b", bgcolor: "#f1f5f9" },
              }}
            >
              Cancelar
            </Button>
          </>
        ) : (
          <IconButton
            size="small"
            onClick={() => setEditando(true)}
            sx={{ color: "#1565c0", "&:hover": { bgcolor: "rgba(21, 101, 192, 0.08)" } }}
          >
            <EditIcon fontSize="small" />
            <Typography variant="button" sx={{ ml: 0.5, color: "#1565c0", fontWeight: 600, textTransform: "none" }}>
              Editar
            </Typography>
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}
